import React, { FC } from "react";
import { I18n } from "react-redux-i18n";
import { FormControl } from "@common/components/form-control";
import { Checkbox } from "@common/components/checkbox";

import Dropdown from "@common/containers/fields/Dropdown";
import { SkipLogicConditionListDto, SkipLogicConditionOperator } from "@common/components/skip-logic/skip-logic.types";
import { getTestAttributes } from "@common/utils/tests";
import { FieldType } from "@common/dto/field/type";
import { ConditionalCheckboxValue } from "./type";
// Add imports for new field types
import { LookupTableInput } from "@modules/spreadsheets/pages/spreadsheet-details/components/spreadsheet-result/components/table/components/body/components/row/components/cell/LookupTableInput";
import { LookupTableDropdown } from "@modules/spreadsheets/pages/spreadsheet-details/components/spreadsheet-result/components/table/components/body/components/row/components/cell/LookupTableDropdown";
import { ImageUploadInput } from "@modules/spreadsheets/pages/spreadsheet-details/components/spreadsheet-result/components/table/components/body/components/row/components/cell/ImageUploadInput";
import { HyperlinkEditor } from "@common/components/hyperlink/HyperlinkEditor";
import { UploadModal } from "@common/components/upload-modal/UploadModal";
import { addBlankOptionToDropdownOptions, normalizeLookupValues } from "@modules/spreadsheets/pages/spreadsheet-details/components/spreadsheet-result/components/table/data-converter";
import { SYSTEM_DATE_FIELD_IDS } from "@modules/spreadsheets/pages/spreadsheet-details/components/spreadsheet-result/components/table/filter-utils";
import { getFolders } from "@common/actions/folders";
import TreeDropdown from "@common/components/TreeDropdown";

import './conditional-field.styles.scss';
type DropdownOption = { value: string; label: string };

type ConditionalFieldProps = {
  condition: SkipLogicConditionListDto;
  onChange: (value: string | number | boolean) => void;
  moduleName: string;
};

export const ConditionalField: FC<ConditionalFieldProps> = ({ condition, onChange, moduleName }) => {
  const [localValue, setLocalValue] = React.useState<string>("");
  // Add state for LookupTable dropdown
  const [showLookupTableDropdown, setShowLookupTableDropdown] = React.useState(false);
  const [lookupDropdownPosition, setLookupDropdownPosition] = React.useState<{
    x: number;
    y: number;
    placement: "bottom" | "top";
  }>({ x: 0, y: 0, placement: "bottom" });
  const lookupInputRef = React.useRef<HTMLDivElement | null>(null);
  
  // Add state for UploadModal (following filter.tsx pattern)
  const [showUploadModal, setShowUploadModal] = React.useState(false);
  const [uploadAccept, setUploadAccept] = React.useState<string[] | undefined>(undefined);
  const [uploadTitle, setUploadTitle] = React.useState<string>("Upload Document");
  const isImageField = condition.field.type === FieldType.image;

  // Add state for folder path field (field ID 73)
  const [folders, setFolders] = React.useState<Array<any>>([]);
  const [foldersLoading, setFoldersLoading] = React.useState(false);
  const [folderNameToIdMap, setFolderNameToIdMap] = React.useState<Record<string, number>>({});
  const [folderIdToNameMap, setFolderIdToNameMap] = React.useState<Record<number, string>>({});
  const isFolderPathField = condition.field.id === 73;

  const conditionOptions = React.useMemo<DropdownOption[]>(() => {
    const mappedOptions = (condition.field.options || []).map((field) => ({
      value: String(field.actualValue ?? ""),
      label: String(field.displayValue ?? ""),
    }));

    return addBlankOptionToDropdownOptions(mappedOptions);
  }, [condition.field.options]);

  // Get lookupValues from condition.field if available (may need type assertion)
  const lookupValues = React.useMemo(() => {
    const field = condition.field as any;
    const rawLookupValues = field.lookupValues;
    
    if (!rawLookupValues) return {};
    
    // Use normalizeLookupValues to handle both array and object formats
    return normalizeLookupValues(rawLookupValues) || {};
  }, [condition.field]);

  React.useEffect(() => {
    if (condition.value !== localValue) {
      let valueToSet = condition.value ?? "";
      const isSystemDateField = condition.field.system && SYSTEM_DATE_FIELD_IDS.includes(condition.field.id);
      if (isSystemDateField && valueToSet) {
        valueToSet = valueToSet.replace(/\//g, '-');
      }

      setLocalValue(valueToSet);
    }
  }, [condition.value, condition.field.system, condition.field.id]);

  // Load folders when folder path field is detected
  React.useEffect(() => {
    if (isFolderPathField) {
      setFoldersLoading(true);
      getFolders({ context: 2 })
        .then((folderData) => {
          setFolders(folderData);

          // Create mapping between folder names and IDs
          const nameToIdMap: Record<string, number> = {};
          const idToNameMap: Record<number, string> = {};

          const processFolders = (folderList: any[]) => {
            folderList.forEach(folder => {
              nameToIdMap[folder.name] = folder.id;
              idToNameMap[folder.id] = folder.name;
              if (folder.children) {
                processFolders(folder.children);
              }
            });
          };

          processFolders(folderData);
          setFolderNameToIdMap(nameToIdMap);
          setFolderIdToNameMap(idToNameMap);
        })
        .catch((error) => {
          console.error('Failed to load folders:', error);
          setFolders([]);
        })
        .finally(() => {
          setFoldersLoading(false);
        });
    }
  }, [isFolderPathField]);

  // Update lookup dropdown position when it opens
  React.useEffect(() => {
    if (showLookupTableDropdown && lookupInputRef.current) {
      const rect = lookupInputRef.current.getBoundingClientRect();
      setLookupDropdownPosition({
        x: rect.left + rect.width / 2,
        y: rect.bottom + 5,
        placement: "bottom",
      });
    }
  }, [showLookupTableDropdown]);

  const handleChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
  }, []);

  const handleDropdownChange = React.useCallback(
    (val: string) => {
      onChange(val);
    },
    [onChange]
  );


  const handleCheckboxChange = React.useCallback(
    (checked: boolean) => {
      onChange(checked ? ConditionalCheckboxValue.checked : ConditionalCheckboxValue.unchecked);
    },
    [onChange]
  );

  const handleTextChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
    },
    [onChange]
  );

  // Handle Image/File input change (for query builder, just text input for filename)
  const handleImageChange = React.useCallback(
    (value: string) => {
      onChange(value);
    },
    [onChange]
  );

  // Handle Hyperlink input change
  const handleHyperlinkChange = React.useCallback(
    (value: string) => {
      onChange(value);
    },
    [onChange]
  );

  // Handle LookupTable selection
  const handleLookupTableSelect = React.useCallback(
    (value: string) => {
      setShowLookupTableDropdown(false);
      onChange(value);
    },
    [onChange]
  );

  // Handle folder selection for folder path field
  const handleFolderChange = React.useCallback(
    (folderId: number, selectedFolder?: any) => {
      // Return the folder name for the query builder
      const folderName = selectedFolder?.name || folderIdToNameMap[folderId] || '';
      onChange(folderName);
    },
    [onChange, folderIdToNameMap]
  );

  // Handle file upload (following filter.tsx pattern - only store filename)
  const handleFileUpload = React.useCallback(
    (file: any) => {
      // Extract filename from file object (file can be File or {file: File})
      const fileName = (file && file.name) 
        ? file.name 
        : (file && file.file && file.file.name) 
          ? file.file.name 
          : undefined;
      
      if (fileName) {
        onChange(fileName);
      }
      setShowUploadModal(false);
      setUploadAccept(undefined);
      setUploadTitle("Upload Document");
    },
    [onChange]
  );

  // Handle upload button click for Image
  const handleImageUploadClick = React.useCallback(() => {
    setShowUploadModal(true);
    setUploadAccept([".jpg", ".jpeg", ".png", ".gif", ".bmp", ".webp"]);
    setUploadTitle("Upload Image");
  }, []);

  // Handle upload button click for Hyperlink
  const handleHyperlinkUploadClick = React.useCallback(() => {
    setShowUploadModal(true);
    setUploadAccept([".pdf", ".doc", ".docx", ".txt", ".jpg", ".png", ".gif", ".xls", ".xlsx"]);
    setUploadTitle("Upload Document");
  }, []);

  const handleUploadModalClose = React.useCallback(() => {
    setShowUploadModal(false);
    setUploadAccept(undefined);
    setUploadTitle("Upload Document");
  }, []);

  const handleUploadModalReject = React.useCallback(() => {
    setShowUploadModal(false);
    setUploadAccept(undefined);
    setUploadTitle("Upload Document");
  }, []);

  const getCurrentField = (fieldTypeId: number) => {

    const checkboxCanHaveOptions = fieldTypeId != FieldType.checkbox;

    const isValidOperatorForTextAndDateFields = [
      SkipLogicConditionOperator.beginsWith,
      SkipLogicConditionOperator.contains,
      SkipLogicConditionOperator.doesNotBeginWith,
      SkipLogicConditionOperator.doesNotContain,
      SkipLogicConditionOperator.doesNotEndWith,
      SkipLogicConditionOperator.endsWith,
      SkipLogicConditionOperator.in,
      SkipLogicConditionOperator.isNotIn,
      SkipLogicConditionOperator.isNotLike,
      SkipLogicConditionOperator.like,
    ].includes(condition.operator as SkipLogicConditionOperator);

    const isValidFieldTypeForTextAndDateFields = [FieldType.date, FieldType.computed, FieldType.dropdown].includes(condition.field.type);

    const normalizedLocalValue = typeof localValue === 'string' ? localValue.toLowerCase() : String(localValue);
    const dropdownValue = conditionOptions.find(condition => condition.value.toString().toLowerCase() === normalizedLocalValue)?.value;
    
    // Check if this is a folder path field (field ID 73) - highest priority
    if (isFolderPathField) {
      // Convert folder name to ID for TreeDropdown value
      const getTreeDropdownValue = () => {
        if (!localValue) return undefined;
        // If value is a folder name, convert to ID
        if (typeof localValue === 'string' && folderNameToIdMap[localValue]) {
          return folderNameToIdMap[localValue];
        }
        // If value is already an ID, return as is
        return localValue;
      };

      if (foldersLoading) {
        return (
          <div className="SkipLogic__conditional-field SkipLogic__conditional-field--loading">
            {I18n.t('Loading folders...')}
          </div>
        );
      }

      if (!folders.length) {
        return (
          <div className="SkipLogic__conditional-field SkipLogic__conditional-field--no-folders">
            {I18n.t('No folders available')}
          </div>
        );
      }

      return (
        <TreeDropdown
          className="SkipLogic__conditional-field SkipLogic__conditional-field--folder"
          items={folders}
          value={getTreeDropdownValue()}
          placeholder={I18n.t('Select Folder')}
          onChange={handleFolderChange}
          {...getTestAttributes(moduleName, "skip-logic-conditional-field", "folder")}
        />
      );
    }
    
    // Check if this is a system date field (highest priority - before other checks)
    const isSystemDateField = condition.field.system && SYSTEM_DATE_FIELD_IDS.includes(condition.field.id);
    
    if (isSystemDateField) {
      return (
        <FormControl
          {...getTestAttributes(moduleName, "skip-logic-conditional-field", "date")}
          value={localValue}
          className="SkipLogic__conditional-field"
          type="date"
          onChange={handleChange}
          onBlur={handleTextChange}
        />
      );
    }
    
    if (isValidOperatorForTextAndDateFields && isValidFieldTypeForTextAndDateFields) {
      return (
        <FormControl
          {...getTestAttributes(moduleName, "skip-logic-conditional-field", "text")}
          value={localValue}
          className="SkipLogic__conditional-field"
          type="text"
          onChange={handleChange}
          onBlur={handleTextChange}
        />
      );
    }


    switch (fieldTypeId) {
      case FieldType.system:
      case FieldType.table:
      case FieldType.dropdown:
        // Don't show dropdown for system date fields (already handled above)
        if (isSystemDateField) {
          return (
            <FormControl
              {...getTestAttributes(moduleName, "skip-logic-conditional-field", "date")}
              value={localValue}
              className="SkipLogic__conditional-field"
              type="date"
              onChange={handleChange}
              onBlur={handleTextChange}
            />
          );
        }
        return (
          <Dropdown
            {...getTestAttributes(moduleName, "skip-logic-conditional-field", "dropdown")}
            placeholder={I18n.t("Select")}
            className="SkipLogic__conditional-field SkipLogic__conditional-field--dropdown"
            options={conditionOptions}
            selectedValue={dropdownValue ?? ""}
            searchable
            onValueChanged={handleDropdownChange}
          />
        );
      case FieldType.checkbox:
        return (
          <Checkbox
            {...getTestAttributes(moduleName, "skip-logic-conditional-field", "checkbox")}
            checked={localValue === ConditionalCheckboxValue.checked}
            className="SkipLogic__conditional-field"
            onChange={handleCheckboxChange}
          />
        );
      case FieldType.number:
      case FieldType.integer:
        return (
          <FormControl
            {...getTestAttributes(moduleName, "skip-logic-conditional-field", "number")}
            value={localValue}
            className="SkipLogic__conditional-field"
            type="number"
            onChange={handleChange}
            onBlur={handleTextChange}
          />
        );
      case FieldType.date:
        return (
          <FormControl
            {...getTestAttributes(moduleName, "skip-logic-conditional-field", "date")}
            value={localValue}
            className="SkipLogic__conditional-field"
            type="date"
            onChange={handleChange}
            onBlur={handleTextChange}
          />
        );
      case FieldType.image:
        return (
          <div ref={lookupInputRef}>
            <ImageUploadInput
              value={localValue}
              onUpload={handleImageUploadClick}
              onChange={handleImageChange}
              className="SkipLogic__conditional-field"
              style={{ backgroundColor: "white", paddingLeft: "4px" }}
            />
          </div>
        );
      case FieldType.lookupTable:
        // Check if lookupValues are available
        const hasLookupValues = lookupValues && Object.keys(lookupValues).length > 0;
        return (
          <div ref={lookupInputRef}>
            <LookupTableInput
              value={localValue}
              onOpen={() => {
                if (hasLookupValues) {
                  setShowLookupTableDropdown(true);
                }
              }}
              className="SkipLogic__conditional-field"
              onBlur={() => setShowLookupTableDropdown(false)}
            />
            {showLookupTableDropdown && hasLookupValues && (
              <LookupTableDropdown
                isOpen={showLookupTableDropdown}
                onClose={() => setShowLookupTableDropdown(false)}
                lookupValues={lookupValues as Record<string, string[]>}
                onSelect={handleLookupTableSelect}
                position={lookupDropdownPosition}
                selectedValue={localValue}
              />
            )}
          </div>
        );
      case FieldType.hyperlink:
        return (
          <HyperlinkEditor
            value={localValue}
            onChange={handleHyperlinkChange}
            onUpload={handleHyperlinkUploadClick}
            className="SkipLogic__conditional-field"
            style={{ paddingLeft: "4px" }}
            autoFocus={false}
          />
        );
      default:
        return (
          <FormControl
            {...getTestAttributes(moduleName, "skip-logic-conditional-field", "text")}
            value={localValue}
            className="SkipLogic__conditional-field"
            type="text"
            onChange={handleChange}
            onBlur={handleTextChange}
          />
        );
    }
  };

  return (
    <>
      {(() => {
        const hasOptions = condition.field.options && condition.field.options.length > 0;
        return getCurrentField(hasOptions ? FieldType.dropdown : condition.field.type);
      })()}
      {/* Close lookup dropdown on outside clicks */}
      {showLookupTableDropdown && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 1 }}
          onMouseDown={(e) => {
            const target = e.target as Element;
            if (!target.closest('.lookup-table-dropdown') && 
                !target.closest('[ref="lookupInputRef"]')) {
              setShowLookupTableDropdown(false);
            }
          }}
        />
      )}
      {/* Upload Modal for Image and Hyperlink (following filter.tsx pattern) */}
      <UploadModal
        isOpen={showUploadModal}
        onClose={handleUploadModalClose}
        onUpload={handleFileUpload}
        onReject={handleUploadModalReject}
        title={uploadTitle}
        accept={uploadAccept || (isImageField ? [".jpg", ".jpeg", ".png", ".gif", ".bmp", ".webp"] : [".pdf", ".doc", ".docx", ".txt", ".jpg", ".png", ".gif", ".xls", ".xlsx"])}
        maxSize={10 * 1024 * 1024} // 10MB
        multiple={false}
      />
    </>
  );
};